import { Component, ErrorInfo, ReactNode, useEffect, useState } from 'react';
import { Link, isRouteErrorResponse, useRouteError } from 'react-router-dom';
import { AlertTriangle, Check, Copy, Home, RotateCw } from 'lucide-react';
import { Button } from './ui';

/**
 * After a deploy the previous build's hashed chunks can disappear while a tab still
 * references them. Browsers word the failure differently, so match all known forms.
 */
const STALE_CHUNK_PATTERN =
  /Failed to fetch dynamically imported module|error loading dynamically imported module|Importing a module script failed|Unable to preload CSS|ChunkLoadError|Loading (?:CSS )?chunk [\w-]+ failed/i;

/** sessionStorage key holding the time of the last automatic reload for a stale chunk */
const STALE_CHUNK_RELOAD_KEY = 'flipiq.stale-chunk-reload-at';
/** A second failure inside this window means the reload did not help: stop and show the error */
const STALE_CHUNK_RELOAD_WINDOW_MS = 30_000;

const errorText = (error: unknown): string => {
  if (!error) return '';
  if (error instanceof Error) return `${error.name}: ${error.message}`;
  if (typeof error === 'string') return error;
  if (typeof error === 'object' && 'message' in error) return String((error as { message: unknown }).message);
  return '';
};

const isStaleChunkError = (error: unknown): boolean => STALE_CHUNK_PATTERN.test(errorText(error));

/**
 * Reloads the page once so the browser fetches the new index.html and chunk names.
 * Returns false (and does nothing) when a reload already happened moments ago, so a
 * genuinely missing file shows the error page instead of looping.
 */
const reloadForStaleChunk = (): boolean => {
  try {
    const last = Number(sessionStorage.getItem(STALE_CHUNK_RELOAD_KEY) || 0);
    if (last && Date.now() - last < STALE_CHUNK_RELOAD_WINDOW_MS) return false;
    sessionStorage.setItem(STALE_CHUNK_RELOAD_KEY, String(Date.now()));
  } catch {
    // Storage blocked (private mode): without a guard a reload could loop, so don't reload
    return false;
  }
  window.location.reload();
  return true;
};

interface Props {
  children: ReactNode;
}

interface State {
  hasError: boolean;
  error: Error | null;
  errorInfo: ErrorInfo | null;
  errorId: string | null;
  copied: boolean;
}

/**
 * App-level error boundary. Rendered above the router (see main.tsx), so the
 * fallback uses plain navigation rather than router links.
 */
class ErrorBoundary extends Component<Props, State> {
  /** True for "chunk not found after a deploy" errors from lazy imports */
  static isStaleChunkError = isStaleChunkError;
  /** One guarded reload to pick up the new build; false when it already tried */
  static reloadForStaleChunk = reloadForStaleChunk;

  private copyTimer: ReturnType<typeof setTimeout> | null = null;

  constructor(props: Props) {
    super(props);
    this.state = {
      hasError: false,
      error: null,
      errorInfo: null,
      errorId: null,
      copied: false,
    };
  }

  static getDerivedStateFromError(error: Error): Partial<State> {
    // Update state so the next render will show the fallback UI
    return {
      hasError: true,
      error,
      errorId: `ERR-${Date.now()}-${Math.random().toString(36).substring(2, 11)}`,
    };
  }

  componentDidCatch(error: Error, errorInfo: ErrorInfo): void {
    // A stale chunk is fixed by loading the new build, not by showing an error
    if (isStaleChunkError(error) && reloadForStaleChunk()) return;

    // Log error details
    const errorId = this.state.errorId || `ERR-${Date.now()}`;

    console.error('ErrorBoundary caught an error:', {
      errorId,
      error: error.toString(),
      errorMessage: error.message,
      errorStack: error.stack,
      componentStack: errorInfo.componentStack,
      timestamp: new Date().toISOString(),
    });

    // Update state with error info
    this.setState({
      errorInfo,
    });

    // Note: Errors are logged to console above and will be captured by CloudWatch Logs
    // when the application logs are streamed to CloudWatch
  }

  componentWillUnmount(): void {
    if (this.copyTimer) clearTimeout(this.copyTimer);
  }

  handleReload = (): void => {
    window.location.reload();
  };

  handleGoHome = (): void => {
    window.location.href = '/';
  };

  handleCopyId = async (): Promise<void> => {
    const { errorId } = this.state;
    if (!errorId || !navigator.clipboard) return;
    try {
      await navigator.clipboard.writeText(errorId);
      this.setState({ copied: true });
      if (this.copyTimer) clearTimeout(this.copyTimer);
      this.copyTimer = setTimeout(() => this.setState({ copied: false }), 2000);
    } catch {
      // Clipboard can be blocked (permissions, insecure context); the ID stays selectable
    }
  };

  render(): ReactNode {
    if (!this.state.hasError) {
      return this.props.children;
    }

    const { error, errorId, copied } = this.state;

    return (
      <main className="flex min-h-screen items-center justify-center bg-canvas px-4 py-12">
        <div className="w-full max-w-md">
          <div className="rounded-xl border border-gray-200 bg-white p-6 text-center sm:p-8">
            <div className="mx-auto flex size-12 items-center justify-center rounded-xl bg-red-50 text-red-600 ring-1 ring-red-100">
              <AlertTriangle aria-hidden className="size-5" />
            </div>

            <h1 className="mt-5 text-xl font-semibold tracking-tight text-gray-900">
              Something went wrong
            </h1>
            <p className="mx-auto mt-2 max-w-sm text-sm leading-relaxed text-gray-600">
              An unexpected error stopped this page from loading. Reloading usually fixes it — your
              saved work is safe.
            </p>

            {errorId && (
              <div className="mt-6 rounded-xl bg-gray-50 p-4 text-left ring-1 ring-inset ring-gray-200">
                <div className="flex items-center justify-between gap-3">
                  <p className="text-xs font-medium text-gray-500">Error reference</p>
                  <button
                    type="button"
                    onClick={this.handleCopyId}
                    aria-label={copied ? 'Error reference copied' : 'Copy error reference'}
                    className="inline-flex items-center gap-1 rounded-md px-1.5 py-0.5 text-xs font-medium text-gray-500 transition-colors hover:bg-gray-100 hover:text-gray-900"
                  >
                    {copied ? <Check aria-hidden className="size-3.5" /> : <Copy aria-hidden className="size-3.5" />}
                    {copied ? 'Copied' : 'Copy'}
                  </button>
                </div>
                <p className="mt-1 select-all break-all font-mono text-meta text-gray-900">{errorId}</p>
                <p className="mt-2 text-xs text-gray-500">Share this ID with support if the problem continues.</p>
              </div>
            )}

            {import.meta.env.DEV && error && (
              <details className="mt-4 rounded-xl bg-red-50 p-4 text-left ring-1 ring-inset ring-red-200">
                <summary className="cursor-pointer text-xs font-semibold text-red-700">
                  Development error details
                </summary>
                <p className="mt-2 break-words font-mono text-xs text-red-700">{error.toString()}</p>
                {error.stack && (
                  <pre className="scrollbar-thin mt-2 max-h-40 overflow-auto whitespace-pre-wrap break-words font-mono text-xs text-red-600">
                    {error.stack}
                  </pre>
                )}
              </details>
            )}

            <div className="mt-6 flex flex-col-reverse gap-2 sm:flex-row sm:justify-center">
              <Button variant="secondary" onClick={this.handleGoHome} leftIcon={<Home />}>
                Go to home
              </Button>
              <Button variant="brand" onClick={this.handleReload} leftIcon={<RotateCw />}>
                Reload page
              </Button>
            </div>
          </div>
        </div>
      </main>
    );
  }
}

/**
 * Route-level error page (`errorElement`). Replaces React Router's developer screen:
 * a stale chunk after a deploy triggers one automatic reload, a 404 says so plainly,
 * and anything else gets a calm panel with Reload and Go to dashboard. When it sits
 * on a child of `UserLayout`, the sidebar and top bar stay in place around it.
 */
export function RouteError() {
  const error = useRouteError();
  const stale = isStaleChunkError(error);
  // Decide once on mount; the reload (when allowed) replaces the page anyway
  const [reloading] = useState(() => stale && reloadForStaleChunk());
  const notFound = isRouteErrorResponse(error) && error.status === 404;

  useEffect(() => {
    if (!reloading && error) console.error('Route error:', error);
  }, [error, reloading]);

  if (reloading) {
    return (
      <div role="status" className="flex min-h-[60vh] items-center justify-center px-4 text-sm text-gray-500">
        Loading the latest version…
      </div>
    );
  }

  const title = notFound
    ? "We couldn't find that page"
    : stale
      ? 'A new version of FlipIQ is available'
      : 'Something went wrong loading this page';
  const description = notFound
    ? 'The link may be out of date, or the page may have moved.'
    : stale
      ? 'Reload to get the latest version. Nothing you saved is lost.'
      : 'Reloading usually fixes it. Nothing you saved is lost.';

  return (
    <div className="flex min-h-[60vh] items-center justify-center px-4 py-12">
      <div className="w-full max-w-md rounded-xl border border-gray-200 bg-white p-6 text-center sm:p-8">
        <h1 className="text-title font-semibold text-gray-900">{title}</h1>
        <p className="mx-auto mt-2 max-w-sm text-sm text-gray-600">{description}</p>
        <div className="mt-6 flex flex-col-reverse gap-2 sm:flex-row sm:justify-center">
          {notFound ? (
            <Button asChild variant="brand" leftIcon={<Home />}>
              <Link to="/user">Go to dashboard</Link>
            </Button>
          ) : (
            <>
              <Button asChild variant="secondary" leftIcon={<Home />}>
                <Link to="/user">Go to dashboard</Link>
              </Button>
              <Button variant="brand" leftIcon={<RotateCw />} onClick={() => window.location.reload()}>
                Reload page
              </Button>
            </>
          )}
        </div>
      </div>
    </div>
  );
}

export default ErrorBoundary;
